import { Link } from '@tanstack/react-router'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import {
  SecondaryButton,
  secondaryLinkClasses,
} from '@workspace/ui/components/dsm/secondary-button'
import {
  ArrowUp,
  ChevronDown,
  Clock,
  FileText,
  Loader2,
  MoreHorizontal,
  Radar,
  Search,
  Share2,
  X,
} from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { useInvestorConfigQuery } from '../../../../hooks/api'
import { copy } from '../../copy/investor-copy'
import {
  useDismissFocusOfferMutation,
  useFocusFromSearchMutation,
  useFounderInvitesQuery,
  useRemoveWatchdogCriterionMutation,
  useReopenPass,
  useRestoreSearchQuery,
  useSearchStartupsMutation,
  useShareStartupMutation,
  useStartupRelationship,
} from '../../hooks/use-investor-assistant'
import { ActionMenu } from '../action-menu'
import { openInviteFor } from '../invites/invite-rows'
import { InviteWindow } from '../invites/invite-window'
import { entryHeading, entryText } from '../relationship/since-card'
import type { MatchedPassage } from '../relationship/startup-side-panel'
import { StopDialog } from '../relationship/stop-dialog'
import type { RelationshipUpdate } from '../relationship/types'
import { lastView, rememberView } from '../remembered-view'
import { BrowseAllLink } from './browse-all-link'
import { cardLine } from './card-line'
import {
  type CriteriaChip,
  canRemoveChip,
  criteriaChips,
  focusSummary,
  removalTerm,
  removeChip,
  type SearchCriteriaShape,
} from './criteria-chips'
import { escapeRegExp, highlightedQuote, searchWordsOf } from './highlight'
import { PassWindow } from './pass-window'
import { PhraseSuggestions } from './phrase-suggestions'
import { isControlClick } from './row-click'
import { type ShareDraft, ShareWindow } from './share-window'

interface PhraseSearchProps {
  /** The saved search in the URL (`?s=`), restored when it is not the answer just given. */
  searchId: string | undefined
  onAnswered: (searchId: string) => void
  /** Opens the startup's side panel; `startFollow` opens its follow window at once (a pass's "Follow instead"). */
  onOpen: (startupId: string, startFollow?: boolean, passage?: MatchedPassage) => void
  onWriteNote: (startupId: string) => void
  /** What the search’s rows show as passed: a pass made here or in the side panel. */
  passes: Record<string, boolean>
  onPassChange: (startupId: string, passed: boolean) => void
  /** What the rows show as followed or stopped: a follow made here or in the side panel. */
  follows: Record<string, boolean>
  onFollowChange: (startupId: string, following: boolean) => void
  /** Opens the watchdog, where the saved focus is set. */
  onEditFocus: () => void
  /** Opens Browse, the full list of startups. */
  onBrowse: () => void
  /** Starts afresh, as New search does: removing the last criterion leaves no search to run. */
  onClear: () => void
}

type Answer = NonNullable<NonNullable<ReturnType<typeof useSearchStartupsMutation>['data']>['data']>

/** Search what founders have shared: a sentence in, startups with the founders' own quotes out. */
export function PhraseSearch({
  searchId,
  onAnswered,
  onOpen,
  onWriteNote,
  passes,
  onPassChange,
  follows,
  onFollowChange,
  onEditFocus,
  onBrowse,
  onClear,
}: PhraseSearchProps) {
  const [text, setText] = useState('')
  const [offerAnsweredFor, setOfferAnsweredFor] = useState<string | undefined>()
  const [undoneWatchdogFor, setUndoneWatchdogFor] = useState<string | undefined>()
  const removeCriterion = useRemoveWatchdogCriterionMutation()
  const { investor } = useInvestorContext()
  // The search just saved is the one the page reopens, even when the answer comes back after the investor left;
  // only while the thread it continues is still the one remembered, so a New search made meanwhile stands.
  const mutation = useSearchStartupsMutation({
    onSaved: (saved, previousId) => {
      if (lastView('search', investor.id).s === previousId)
        rememberView('search', investor.id, { s: saved })
    },
  })
  const focus = useFocusFromSearchMutation()
  const dismiss = useDismissFocusOfferMutation()
  const share = useShareStartupMutation()
  const reopen = useReopenPass()
  const [passing, setPassing] = useState<{ id: string; name: string } | null>(null)
  const invites = useFounderInvitesQuery()
  const [inviting, setInviting] = useState<string | null>(null)
  const [shareDraft, setShareDraft] = useState<ShareDraft | null>(null)
  const [stopping, setStopping] = useState<{ id: string; name: string; waiting: boolean } | null>(
    null
  )
  // Undo puts back a pass made in this search; it is the same reopen the Passed tab offers.
  const undoPass = (startupId: string) =>
    reopen.mutate({ path: { startupId } }, { onSuccess: () => onPassChange(startupId, false) })
  // Undo takes out the criteria this search added to the watchdog; the notice then goes.
  const undoWatchdog = (criterionIds: string[]) => {
    for (const criterionId of criterionIds) removeCriterion.mutate({ path: { criterionId } })
    setUndoneWatchdogFor(answer?.searchId)
  }
  const shareStartup = (startupId: string) =>
    share.mutate({ path: { startupId } }, { onSuccess: (response) => setShareDraft(response.data) })
  const stopFollowing = (result: Answer['results'][number]) =>
    setStopping({
      id: result.id,
      name: result.name,
      waiting: result.state === 'WAITING_CHECK' || result.state === 'WAITING_PLACE',
    })
  const config = useInvestorConfigQuery()
  const latest = mutation.data?.data
  // The answer just given is already here; restoring it again would run the same search twice.
  const restored = useRestoreSearchQuery(searchId === latest?.searchId ? undefined : searchId)
  // A pending answer is run again until the firm check completes; then its quotes replace the summary rows.
  const pendingId = latest?.kind === 'pending' ? latest.searchId : undefined
  const awaited = useRestoreSearchQuery(pendingId)
  const settled = awaited.data?.data?.kind === 'pending' ? undefined : awaited.data?.data
  // The last answer that ran: over today's limit it stays on screen, with the composer off.
  const lastAnswer = useRef<Answer | undefined>(undefined)
  // The answer a running search continues: its question stays in the thread until the new answer arrives (a new run
  // clears the mutation's own data).
  const continued = useRef<Answer | undefined>(undefined)
  const pendingRef = useRef<HTMLDivElement>(null)
  // A search over today's limit has no id in the URL; with no earlier answer its banner is what shows.
  const answer: Answer | undefined =
    mutation.isPending && continued.current
      ? continued.current
      : latest?.kind === 'limit'
        ? (lastAnswer.current ?? latest)
        : searchId && latest?.searchId === searchId
          ? latest.kind === 'pending'
            ? (settled ?? latest)
            : latest
          : restored.data?.data
  const limitReached = latest?.kind === 'limit'
  const regionLabel = (code: string) =>
    config.data?.investmentRegions?.find((option) => option.value === code)?.label ?? code

  // Each search continues the answer shown, so the thread (its earlier questions) is saved with it and restores whole.
  const run = (
    body: {
      text: string
      criteria?: Answer['criteria']
      base?: Answer['criteria']
    },
    /** The message the thread shows when it is not the text searched (a picked refinement's label). */
    shown?: string
  ) => {
    const previousId = answer && answer.kind !== 'limit' ? answer.searchId : undefined
    continued.current = answer
    mutation.mutate(
      { body: { ...body, ...(previousId && { previousId }), ...(shown && { shown }) } },
      {
        onSuccess: (response) => {
          if (response.data.kind !== 'limit') lastAnswer.current = response.data
          if (response.data.searchId) onAnswered(response.data.searchId)
        },
      }
    )
  }
  // The new question and its spinner are the last message: bring them into view when a search starts. Done before the
  // browser paints and without animation, so the shorter page (the previous results are gone) is never shown scrolled
  // past its end, which Safari paints as an empty screen.
  useLayoutEffect(() => {
    if (mutation.isPending) pendingRef.current?.scrollIntoView({ block: 'center' })
  }, [mutation.isPending])
  // A typed "Remove …" takes out the chips it names, with the rest of the answer's criteria, as a chip removal does.
  const removeTyped = (query: string): boolean => {
    const term = removalTerm(query)
    if (!answer || !term) return false
    const named = criteriaChips(answer.criteria).filter((chip) =>
      termNames(term, chip, regionLabel)
    )
    if (named.length === 0) return false
    // Last first: a chip's key is its position in its list, so the earlier positions stay right.
    const criteria = [...named].reverse().reduce(removeChip, answer.criteria)
    // A search runs on at least one criterion: removing them all starts afresh.
    if (criteriaChips(criteria).length === 0) onClear()
    else run({ text: query, criteria })
    return true
  }
  const submit = () => {
    const query = text.trim()
    if (!query) return
    // After an answer, the text refines it: what it does not name keeps the criteria the answer shows.
    if (!removeTyped(query))
      run(
        answer && answer.kind !== 'limit' ? { text: query, base: answer.criteria } : { text: query }
      )
    setText('')
  }
  // A removed chip reruns the same search with the criteria left: the parser is not asked again.
  // The last chip has nothing left to run on, so removing it starts afresh.
  const removeCriteriaChip = (chip: CriteriaChip) => {
    if (!answer) return
    // The thread keeps the message it showed: the same question again is not repeated.
    if (canRemoveChip(answer.criteria))
      run({ text: answer.query, criteria: removeChip(answer.criteria, chip) }, answer.question)
    else onClear()
  }

  // After an answer the form is a dock at the foot of the page, so the next refine is one keystroke away.
  const form = (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
      className="flex items-center gap-2.5 rounded-full border border-primary bg-[#0F0F10] py-2 pl-4 pr-2 shadow-[0_0_0_3px_rgba(249,115,22,0.12)] focus-within:shadow-[0_0_0_3px_rgba(249,115,22,0.28)]"
    >
      <Search className="size-4 flex-none text-muted-foreground" />
      <input
        id={COMPOSER_INPUT_ID}
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={500}
        disabled={limitReached}
        placeholder={
          limitReached && latest?.limit.resetsAt
            ? copy('search.limit.placeholder', {
                when: resetLabel(new Date(latest.limit.resetsAt)),
              })
            : copy(answer ? 'search.phrase.refine' : 'search.phrase.placeholder')
        }
        aria-label={copy(answer ? 'search.phrase.refine' : 'search.input.label')}
        className="min-w-0 flex-1 bg-transparent py-2 text-[15px] text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-50"
      />
      <button
        type="submit"
        aria-label={copy('search.phrase.submit')}
        disabled={mutation.isPending || limitReached || text.trim() === ''}
        className="grid size-10 flex-none place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary-hover disabled:opacity-50"
      >
        <ArrowUp className="size-4" />
      </button>
    </form>
  )
  return (
    <>
      <section aria-label={copy('search.phrase.label')} className="space-y-3">
        {!answer && form}
        {!answer && <p className="text-xs text-muted-foreground">{copy('search.private.note')}</p>}
        {mutation.isError && (
          <p role="alert" className="text-sm text-muted-foreground">
            {copy('search.phrase.error')}
          </p>
        )}
        {restored.isError && (
          <p role="alert" className="text-sm text-muted-foreground">
            {copy('search.restore.error')}
          </p>
        )}
        {!answer && !mutation.isPending && (
          <PhraseSuggestions
            onRun={(item) => run({ text: item.label, criteria: item.criteria })}
            onEditFocus={onEditFocus}
          />
        )}
        {!answer && <BrowseAllLink onBrowse={onBrowse} />}
        {answer?.thread.map((question, index) => (
          <QueryBubble key={`${index}:${question}`} text={question} />
        ))}
        {/* While the next search runs, the answer it continues shows only its question: its results go until the new
            answer replaces them. */}
        {answer && mutation.isPending && answer.kind !== 'limit' && (
          <QueryBubble text={answer.question} />
        )}
        {answer && !mutation.isPending && (
          <PhraseAnswer
            answer={answer}
            onOpen={onOpen}
            onWriteNote={onWriteNote}
            onRemoveChip={removeCriteriaChip}
            busy={mutation.isPending}
            passes={passes}
            follows={follows}
            onPass={setPassing}
            onUndo={undoPass}
            onShare={shareStartup}
            onInvite={setInviting}
            onStop={stopFollowing}
            onClosest={(criteria, label) => run({ text: answer.query, criteria }, label)}
            onViewCriteria={onEditFocus}
            onUndoWatchdog={undoWatchdog}
            watchdogUndone={undoneWatchdogFor === answer.searchId}
            regionLabel={regionLabel}
          />
        )}
        {/* The next question goes under the questions before it. */}
        {mutation.isPending && mutation.variables?.body.text && (
          <div ref={pendingRef} className="space-y-4" aria-live="polite">
            <QueryBubble text={mutation.variables.body.shown ?? mutation.variables.body.text} />
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {copy('search.searching')}
            </p>
          </div>
        )}
        {answer?.focusOffer && offerAnsweredFor !== answer.searchId && (
          <FocusOffer
            criteria={answer.criteria}
            summaryOf={(criteria) =>
              focusSummary(criteria, {
                stage: (stage) =>
                  copy(stage === 'preseed' ? 'search.criteria.preseed' : 'search.criteria.seed'),
                region: regionLabel,
              })
            }
            chipLabel={(chip) => chipText(chip, regionLabel)}
            busy={focus.isPending || dismiss.isPending}
            onUse={(criteria) =>
              focus.mutate(
                { body: { criteria } },
                { onSuccess: () => setOfferAnsweredFor(answer.searchId) }
              )
            }
            onNotNow={() =>
              dismiss.mutate({}, { onSuccess: () => setOfferAnsweredFor(answer.searchId) })
            }
          />
        )}
        <PassWindow
          startup={passing}
          onClose={() => setPassing(null)}
          onPassed={(startupId) => onPassChange(startupId, true)}
          onFollowInstead={(startupId) => {
            setPassing(null)
            onOpen(startupId, true)
          }}
        />
        <ShareWindow draft={shareDraft} onClose={() => setShareDraft(null)} />
        {inviting !== null && (
          <InviteWindow
            open
            startup={inviting}
            existing={openInviteFor(invites.data ?? [], inviting)}
            onClose={() => setInviting(null)}
          />
        )}
        {stopping && (
          <StopDialog
            startupId={stopping.id}
            startupName={stopping.name}
            waiting={stopping.waiting}
            onClose={() => setStopping(null)}
            onStopped={() => onFollowChange(stopping.id, false)}
          />
        )}
      </section>
      {answer && <div className="sticky bottom-4 z-10 pt-2">{form}</div>}
    </>
  )
}

/** A result's newest passage date, as a time; a result with no dated passage sorts last. */
const newestPassage = (result: Answer['results'][number]): number =>
  Math.max(0, ...result.passages.map((passage) => Date.parse(passage.date ?? '') || 0))

function sortedResults(results: Answer['results'], sort: ResultSort): Answer['results'] {
  return [...results].sort((a, b) =>
    sort === 'name' ? a.name.localeCompare(b.name) : newestPassage(b) - newestPassage(a)
  )
}

/** A chip's text as the thread shows it: a stage, a region's name, a sector, or a phrase in quotes. */
function chipText(chip: CriteriaChip, regionLabel: (code: string) => string): string {
  if (chip.kind === 'stage') {
    return chip.stages
      .map((stage) =>
        copy(stage === 'preseed' ? 'search.criteria.preseed' : 'search.criteria.seed')
      )
      .join(' or ')
  }
  if (chip.kind === 'region') return regionLabel(chip.region)
  if (chip.kind === 'sector') return chip.sector
  return copy('search.closest.phrase', { value: chip.text })
}

/** Whether a removal's term names the chip: a phrase as a whole word of it, a stage, region or sector by its name. */
function termNames(
  term: string,
  chip: CriteriaChip,
  regionLabel: (code: string) => string
): boolean {
  const lower = term.toLowerCase()
  if (chip.kind === 'phrase') {
    return new RegExp(`(^|\\W)${escapeRegExp(chip.text.toLowerCase())}(\\W|$)`).test(lower)
  }
  return lower === chipText(chip, regionLabel).toLowerCase()
}

/** The name of the criterion a closest search drops, as the chips name it. */
function closestLabel(
  dropped: { kind: 'stage' | 'region' | 'sector' | 'phrase'; value: string },
  regionLabel: (code: string) => string
): string {
  if (dropped.kind === 'stage') return copy('search.next.anyStage')
  if (dropped.kind === 'region')
    return copy('search.closest.region', { region: regionLabel(dropped.value) })
  if (dropped.kind === 'phrase') return copy('search.next.removePhrase', { text: dropped.value })
  return copy('search.next.remove', { chip: dropped.value })
}

/** When a search over today's limit can run again, as "13 Oct at 01:00" in Prague time. */
function resetLabel(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Europe/Prague',
  }).formatToParts(at)
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? ''
  return `${part('day')} ${part('month')} at ${part('hour')}:${part('minute')}`
}

/** The amber note while the account check runs: the search is saved, and quotes come after the check. */
function PendingBanner() {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
      <Clock className="mt-0.5 size-4 flex-none text-amber-500" aria-hidden />
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{copy('search.pending.title')}</p>
        <p className="text-sm text-muted-foreground">{copy('search.pending.body')}</p>
      </div>
    </div>
  )
}

const COMPOSER_INPUT_ID = 'search-composer'

/** Puts the cursor in the search box, scrolled into view: the box describes what the investor is looking for. */
function focusComposer() {
  const input = document.getElementById(COMPOSER_INPUT_ID)
  input?.scrollIntoView({ block: 'center' })
  input?.focus()
}

/** The order of a search's results: the newest passage first, or by name. */
type ResultSort = 'latest' | 'name'

type AnswerBodyProps = {
  answer: Answer
  onOpen: (startupId: string, startFollow?: boolean, passage?: MatchedPassage) => void
  onWriteNote: (startupId: string) => void
  /** Runs a pill under the results; its label is what the thread shows as the investor's message. */
  onClosest: (criteria: Answer['criteria'], label: string) => void
  onViewCriteria: () => void
  onUndoWatchdog: (criterionIds: string[]) => void
  watchdogUndone: boolean
  regionLabel: (code: string) => string
  onRemoveChip: (chip: CriteriaChip) => void
  busy: boolean
  passes: Record<string, boolean>
  follows: Record<string, boolean>
  onPass: (startup: { id: string; name: string }) => void
  onUndo: (startupId: string) => void
  onShare: (startupId: string) => void
  onStop: (result: Answer['results'][number]) => void
  onInvite: (name: string) => void
}

/** The search text as a chat bubble on the right. */
function QueryBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <p className="max-w-md rounded-2xl rounded-br-sm bg-elevated px-4 py-2.5 text-sm text-foreground">
        {text}
      </p>
    </div>
  )
}

/** The answer: the search text as a chat bubble on the right, then what the search found. */
function PhraseAnswer(props: AnswerBodyProps) {
  const { answer } = props
  return (
    <div className="space-y-4">
      {answer.kind !== 'limit' && <QueryBubble text={answer.question} />}
      <AnswerBody {...props} />
    </div>
  )
}

function AnswerBody({
  answer,
  onOpen,
  onWriteNote,
  onRemoveChip,
  busy,
  passes,
  follows,
  onPass,
  onUndo,
  onShare,
  onStop,
  onInvite,
  onClosest,
  onViewCriteria,
  onUndoWatchdog,
  watchdogUndone,
  regionLabel,
}: AnswerBodyProps) {
  const [sort, setSort] = useState<ResultSort>('latest')
  if (answer.kind === 'limit') {
    return <p className="text-sm text-muted-foreground">{copy('search.limit.reached')}</p>
  }
  if (answer.kind === 'name-missing') {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-foreground">{answer.query}</h3>
        <p className="text-sm text-muted-foreground">
          {copy('search.empty.name', { query: answer.query })}
        </p>
        <section className="space-y-3 rounded-xl border border-border bg-card/30 p-4">
          <h3 className="text-sm font-semibold text-foreground">
            {copy('search.invite.heading', { startup: answer.query })}
          </h3>
          <p className="text-sm text-muted-foreground">{copy('search.invite.body')}</p>
          <NameMissingInvite startup={answer.query} onInvite={onInvite} />
        </section>
        <p className="text-sm text-muted-foreground">{copy('search.name.describe')}</p>
        <SecondaryButton type="button" size="sm" onClick={focusComposer}>
          {copy('search.name.byWhat')}
        </SecondaryButton>
      </div>
    )
  }
  if (answer.kind === 'name') {
    const [first] = answer.results
    return first ? (
      <ul className="space-y-3">
        <NameResultCard
          result={first}
          passes={passes}
          follows={follows}
          phrases={answer.criteria.phrases}
          onOpen={onOpen}
          onWriteNote={onWriteNote}
          onPass={onPass}
          onUndo={onUndo}
          onShare={onShare}
          onStop={onStop}
        />
      </ul>
    ) : null
  }
  if (answer.kind === 'empty') {
    return (
      <div className="space-y-4">
        <CriteriaRow
          answer={answer}
          onRemove={onRemoveChip}
          disabled={busy}
          regionLabel={regionLabel}
        />
        <p className="text-sm text-foreground">{copy('search.phrase.empty')}</p>
        {answer.closest.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-muted-foreground">
              {copy('search.closest.heading')}
            </span>
            {answer.closest.map((item) => (
              <button
                key={`${item.dropped.kind}|${item.dropped.value}`}
                type="button"
                disabled={busy}
                onClick={() => onClosest(item.criteria, closestLabel(item.dropped, regionLabel))}
                className="rounded-full border border-border px-3 py-1.5 text-left text-[12.5px] text-foreground transition-colors hover:border-foreground/40 disabled:opacity-50"
              >
                {closestLabel(item.dropped, regionLabel)}
                <span className="ml-1.5 tabular-nums text-muted-foreground">{item.count}</span>
              </button>
            ))}
          </div>
        )}
        {answer.watchdogChange && !watchdogUndone && (
          <WatchdogNotice
            change={answer.watchdogChange}
            regionLabel={regionLabel}
            busy={busy}
            onView={onViewCriteria}
            onUndo={() => onUndoWatchdog(answer.watchdogChange?.added.map((item) => item.id) ?? [])}
          />
        )}
      </div>
    )
  }
  return (
    <div className="space-y-4">
      {answer.kind === 'pending' && <PendingBanner />}
      <CriteriaRow
        answer={answer}
        onRemove={onRemoveChip}
        disabled={busy}
        regionLabel={regionLabel}
      />
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-foreground">{countLineOf(answer, regionLabel)}</p>
        <ActionMenu
          label={copy('search.phrase.label')}
          actions={[
            { key: 'latest', title: copy('search.sort.latest'), onSelect: () => setSort('latest') },
            { key: 'name', title: copy('search.sort.name'), onSelect: () => setSort('name') },
          ]}
          trigger={
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {copy(sort === 'latest' ? 'search.sort.latest' : 'search.sort.name')}
              <ChevronDown className="size-3.5" />
            </button>
          }
        />
      </div>
      {answer.kind === 'opinion' && (
        <p className="text-sm text-foreground">{opinionIntroOf(answer, regionLabel)}</p>
      )}
      {answer.kind === 'opinion' &&
        (answer.nextSearches.length > 0 ? (
          <NextSearches
            items={answer.nextSearches}
            heading={copy('search.opinion.narrow')}
            regionLabel={regionLabel}
            busy={busy}
            onRun={onClosest}
          />
        ) : (
          <p className="text-sm font-semibold text-foreground">{copy('search.opinion.narrow')}</p>
        ))}
      <ul className="space-y-3">
        {sortedResults(answer.results, sort).map((result) => (
          <ResultCard
            key={result.id}
            result={result}
            passes={passes}
            follows={follows}
            phrases={answer.criteria.phrases}
            onOpen={onOpen}
            onWriteNote={onWriteNote}
            onPass={onPass}
            onUndo={onUndo}
            onShare={onShare}
            onStop={onStop}
          />
        ))}
      </ul>
      {answer.kind !== 'opinion' && answer.nextSearches.length > 0 && (
        <NextSearches
          items={answer.nextSearches}
          heading={copy('search.next.heading')}
          regionLabel={regionLabel}
          busy={busy}
          onRun={onClosest}
        />
      )}
      {answer.watchdogChange && !watchdogUndone && (
        <WatchdogNotice
          change={answer.watchdogChange}
          regionLabel={regionLabel}
          busy={busy}
          onView={onViewCriteria}
          onUndo={() => onUndoWatchdog(answer.watchdogChange?.added.map((item) => item.id) ?? [])}
        />
      )}
    </div>
  )
}

/** The "Searching for" chips: each one can be removed, and the search runs again without it. */
/** "Matching passages found in 3 of 11 pre-seed and seed profiles in Europe.", from the criteria the search ran with. */
/** The profiles a search covers and the region it names, as the count line and the opinion intro say them. */
function profilesOf(answer: Answer, regionLabel: (code: string) => string) {
  const { stages, regions = [] } = answer.criteria
  const profiles =
    stages.length === 2
      ? copy('search.profiles.both')
      : stages.length === 1
        ? copy(stages[0] === 'preseed' ? 'search.profiles.preseed' : 'search.profiles.seed')
        : copy('search.profiles.any')
  const region =
    regions.length > 0
      ? ` ${copy('search.region.in', { region: regions.map(regionLabel).join(' or ') })}`
      : ''
  return { profiles, region }
}

function countLineOf(answer: Answer, regionLabel: (code: string) => string): string {
  const { profiles, region } = profilesOf(answer, regionLabel)
  if (answer.kind === 'pending') {
    return copy('search.phrase.countPending', {
      matched: answer.countLine.matched,
      profiles,
      region,
    })
  }
  if (answer.countLine.of === 1) return copy('search.phrase.countOne')
  return copy('search.phrase.count', {
    matched: answer.countLine.matched,
    of: answer.countLine.of,
    profiles,
    region,
  })
}

/** "Here are the 3 climate tech startups in Europe on SeedForge, with the founders' own evidence." */
function opinionIntroOf(answer: Answer, regionLabel: (code: string) => string): string {
  const { profiles, region } = profilesOf(answer, regionLabel)
  return copy('search.opinion.intro', { matched: answer.countLine.matched, profiles, region })
}

function CriteriaRow({
  answer,
  onRemove,
  disabled,
  regionLabel,
}: {
  answer: Answer
  onRemove: (chip: CriteriaChip) => void
  disabled: boolean
  regionLabel: (code: string) => string
}) {
  const chips = criteriaChips(answer.criteria)
  if (chips.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">{copy('search.criteria.label')}</span>
      {chips.map((chip) => {
        const label = chipLabel(chip, regionLabel)
        return (
          <span
            key={chip.key}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-card/30 py-1 pl-3 pr-1 text-sm text-foreground"
          >
            {label}
            <button
              type="button"
              disabled={disabled}
              aria-label={copy('search.criteria.remove', { chip: label })}
              onClick={() => onRemove(chip)}
              className="rounded-full p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <X className="size-3.5" />
            </button>
          </span>
        )
      })}
    </div>
  )
}

function chipLabel(chip: CriteriaChip, regionLabel: (code: string) => string): string {
  if (chip.kind === 'phrase') return copy('search.criteria.phrase', { text: chip.text })
  if (chip.kind === 'sector') return chip.sector
  if (chip.kind === 'region') return regionLabel(chip.region)
  return chip.stages.map(stageLabel).join(' or ')
}

function stageLabel(stage: 'preseed' | 'seed'): string {
  return stage === 'preseed' ? copy('search.criteria.preseed') : copy('search.criteria.seed')
}

/** Offered once after a search that named something: the search becomes the investor's focus, or "Not now". */
function FocusOffer({
  criteria,
  summaryOf,
  chipLabel,
  busy,
  onUse,
  onNotNow,
}: {
  criteria: SearchCriteriaShape
  summaryOf: (criteria: SearchCriteriaShape) => string
  chipLabel: (chip: CriteriaChip) => string
  busy: boolean
  onUse: (criteria: SearchCriteriaShape) => void
  onNotNow: () => void
}) {
  // "Edit before saving" lets the investor drop a chip before it becomes their focus; nothing is saved until "Use".
  const [draft, setDraft] = useState(criteria)
  const [editing, setEditing] = useState(false)
  return (
    <section
      aria-label={copy('search.focus.question')}
      className="space-y-3 rounded-xl border border-border bg-card/30 p-4"
    >
      <p className="text-sm font-semibold text-foreground">{copy('search.focus.question')}</p>
      <p className="text-sm text-muted-foreground">
        {copy('search.focus.describe', { summary: summaryOf(draft) })}
      </p>
      {editing && (
        <ul className="flex flex-wrap gap-2">
          {criteriaChips(draft).map((chip) => (
            <li
              key={chip.key}
              className="flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-foreground"
            >
              {chipLabel(chip)}
              <button
                type="button"
                disabled={!canRemoveChip(draft)}
                aria-label={copy('search.criteria.remove', { chip: chipLabel(chip) })}
                onClick={() => setDraft(removeChip(draft, chip))}
                className="text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onUse(draft)}
          className="rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {copy('search.focus.use')}
        </button>
        {!editing && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing(true)}
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-foreground hover:bg-white/[0.04] disabled:opacity-50"
          >
            {copy('search.focus.edit')}
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onNotNow}
          className="rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          {copy('search.focus.notNow')}
        </button>
      </div>
    </section>
  )
}

type Result = Answer['results'][number]

/** The passage the panel shows under "What matched your search", with the words to mark in it. */
function matchedOf(
  passage: Result['passages'][number] | undefined,
  phrases: string[]
): MatchedPassage | undefined {
  return passage && { ...passage, words: searchWordsOf(phrases) }
}

/** "Deck, 2 documents, 1 email update": what the startup has published. */
function publishedLine(published: { deck: boolean; documents: number; updates: number }): string {
  const parts: string[] = []
  if (published.deck) parts.push(copy('search.name.deck'))
  if (published.documents > 0) parts.push(copy('search.name.documents', { n: published.documents }))
  if (published.updates > 0) {
    parts.push(
      published.updates === 1
        ? copy('search.name.update.one')
        : copy('search.name.update.many', { n: published.updates })
    )
  }
  return parts.join(', ')
}

type NextSearch = Answer['nextSearches'][number]

/** A name answer’s card, with the founder’s newest update from the startup’s relationship. */
function NameResultCard(props: Omit<React.ComponentProps<typeof ResultCard>, 'latest'>) {
  const relationship = useStartupRelationship(props.result.id)
  return (
    <ResultCard
      {...props}
      latest={relationship.data?.latestUpdates[0] ?? null}
      published={relationship.data?.summary ?? null}
    />
  )
}

/** The invite on a name nobody has: the orange invite, or Open invite when the investor already invited that founder. */
function NameMissingInvite({
  startup,
  onInvite,
}: {
  startup: string
  onInvite: (startup: string) => void
}) {
  const invites = useFounderInvitesQuery()
  const invited = openInviteFor(invites.data ?? [], startup)
  return (
    <CtaButton type="button" onClick={() => onInvite(startup)}>
      {invited ? copy('invites.open') : copy('search.invite.button', { startup })}
    </CtaButton>
  )
}

/** One startup in an answer: its name and what it does, the founder's quote that matched, and what the investor can do. */
function ResultCard({
  result,
  passes,
  follows,
  phrases,
  latest,
  published,
  onOpen,
  onWriteNote,
  onPass,
  onUndo,
  onShare,
  onStop,
}: {
  result: Result
  /** What the startup has published: the footer of a name answer. */
  published?: { deck: boolean; documents: number; updates: number } | null
  /** The founder’s newest published update, shown as “Latest from the founder” on a name answer. */
  latest?: RelationshipUpdate | null
  passes: Record<string, boolean>
  follows: Record<string, boolean>
  phrases: string[]
  onOpen: (startupId: string, startFollow?: boolean, passage?: MatchedPassage) => void
  onWriteNote: (startupId: string) => void
  onPass: (startup: { id: string; name: string }) => void
  onUndo: (startupId: string) => void
  onShare: (startupId: string) => void
  onStop: (result: Result) => void
}) {
  const passed = passes[result.id] ?? result.passed
  // Following or waiting to follow: the card offers to stop, not to pass.
  const followHere = follows[result.id]
  const followed = followHere ?? (result.state !== 'NONE' && result.state !== 'ENDED_BY_FOUNDER')
  // Stopped here, or not followed yet and in the state the search answered with.
  const canFollow = followHere === false || (followHere === undefined && result.state === 'NONE')
  const more = [
    ...(result.shareable
      ? [
          {
            key: 'share',
            icon: Share2,
            title: copy('search.result.share'),
            onSelect: () => onShare(result.id),
          },
        ]
      : []),
    ...(!followed && !passed
      ? [
          {
            key: 'pass',
            icon: X,
            title: copy('search.result.pass'),
            onSelect: () => onPass({ id: result.id, name: result.name }),
          },
        ]
      : []),
    ...(followed
      ? [
          {
            key: 'stop',
            icon: X,
            title: copy('menu.stopFollowing'),
            destructive: true,
            onSelect: () => onStop(result),
          },
        ]
      : []),
  ]
  return (
    <li
      className="cursor-pointer overflow-hidden rounded-xl border border-border bg-card/30 p-4 transition-colors hover:border-foreground/30"
      onClick={(event) => {
        if (!isControlClick(event)) onOpen(result.id, false, matchedOf(result.passages[0], phrases))
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target === event.currentTarget)
          onOpen(result.id, false, matchedOf(result.passages[0], phrases))
      }}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-elevated text-sm font-semibold text-foreground/80"
        >
          {result.name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onOpen(result.id, false, matchedOf(result.passages[0], phrases))}
            className="text-left text-sm font-semibold text-foreground hover:underline"
          >
            {result.name}
          </button>
          {cardLine(result) !== '' && (
            <p className="truncate text-xs text-muted-foreground">{cardLine(result)}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {followed && (
            <span className="rounded-full bg-elevated px-2.5 py-1 text-xs text-muted-foreground">
              {copy(
                result.state === 'WAITING_CHECK' || result.state === 'WAITING_PLACE'
                  ? 'search.status.waiting'
                  : 'search.status.following'
              )}
            </span>
          )}
          {passed && (
            <span className="text-xs text-muted-foreground">{copy('search.result.passed')}</span>
          )}
          {passes[result.id] === true && (
            <button
              type="button"
              onClick={() => onUndo(result.id)}
              className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
            >
              {copy('search.result.undo')}
            </button>
          )}
          {canFollow && !passed && (
            <SecondaryButton
              type="button"
              size="sm"
              onClick={() => onOpen(result.id, true, matchedOf(result.passages[0], phrases))}
            >
              {copy('search.result.follow')}
            </SecondaryButton>
          )}
          {/* A concrete startup has its footer: Open profile is there, so the header keeps only Follow and Pass. */}
          {result.profileLinkToken && !published && (
            <OpenButton
              name={result.name}
              token={result.profileLinkToken}
              label={copy('search.result.open')}
            />
          )}
          {more.length > 0 && (
            <ActionMenu
              label={copy('search.result.more', { name: result.name })}
              actions={more}
              trigger={
                <button
                  type="button"
                  aria-label={copy('search.result.more', { name: result.name })}
                  className="grid size-8 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
                >
                  <MoreHorizontal className="size-4" />
                </button>
              }
            />
          )}
        </div>
      </div>
      {latest && (
        <section className="mt-4 space-y-2 border-t border-border pt-4">
          <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {copy('search.name.latest')}
          </h4>
          <blockquote className="rounded-lg bg-[#0F0F10] px-3 py-2.5 text-sm leading-relaxed text-foreground/80">
            {entryText(latest)}
          </blockquote>
          <span className="inline-flex items-center gap-1.5 rounded-[5px] border border-emerald-500/35 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-emerald-500">
            {entryHeading(latest)}
          </span>
        </section>
      )}
      {published && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex flex-wrap gap-2">
            {result.profileLinkToken && (
              <OpenButton
                name={result.name}
                token={result.profileLinkToken}
                label={copy('search.name.profile')}
              />
            )}
            <SecondaryButton type="button" size="sm" onClick={() => onWriteNote(result.id)}>
              {copy('search.name.notes')}
            </SecondaryButton>
          </div>
          <span className="text-xs text-muted-foreground">{publishedLine(published)}</span>
        </div>
      )}
      {result.passages.map((passage) => (
        <div key={`${passage.source}|${passage.date ?? ''}|${passage.text.slice(0, 40)}`}>
          <blockquote className="mt-3 rounded-lg bg-[#0F0F10] px-3 py-2.5 text-sm leading-relaxed text-foreground/80">
            {'“'}
            {highlightedQuote(passage.text, searchWordsOf(phrases))}
            {'”'}
          </blockquote>
          <div className="mt-2">
            <span className="inline-flex items-center gap-1.5 rounded-[5px] border border-emerald-500/35 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-emerald-500">
              <FileText className="size-3" aria-hidden />
              {passage.source}
              {passage.date ? ` · ${passage.date}` : ''}
            </span>
          </div>
        </div>
      ))}
    </li>
  )
}

/** "Change the search": the refinements of an answer, each with how many startups it would show. */
function NextSearches({
  items,
  heading,
  regionLabel,
  busy,
  onRun,
}: {
  items: NextSearch[]
  heading: string
  regionLabel: (code: string) => string
  busy: boolean
  onRun: (criteria: Answer['criteria'], label: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs text-muted-foreground">{heading}</span>
      {items.map((item) => (
        <button
          key={`${item.change}|${item.value}`}
          type="button"
          disabled={busy}
          onClick={() => onRun(item.criteria, nextLabel(item, regionLabel))}
          className="rounded-full border border-border px-3 py-1.5 text-left text-[12.5px] text-foreground transition-colors hover:border-foreground/40 disabled:opacity-50"
        >
          {nextLabel(item, regionLabel)}
          <span className="ml-1.5 tabular-nums text-muted-foreground">{item.count}</span>
        </button>
      ))}
    </div>
  )
}

function nextLabel(item: NextSearch, regionLabel: (code: string) => string): string {
  if (item.change === 'only-stage') {
    const stage =
      item.value === 'preseed' ? copy('search.criteria.preseed') : copy('search.criteria.seed')
    return copy('search.next.only', { stage: stage.toLowerCase() })
  }
  if (item.change === 'any-stage') return copy('search.next.anyStage')
  if (item.change === 'remove-phrase') return copy('search.next.removePhrase', { text: item.value })
  if (item.change === 'remove-region')
    return copy('search.next.remove', { chip: regionLabel(item.value) })
  return copy('search.next.remove', { chip: item.value })
}

/** "Your watchdog criteria were updated": what this search added to the watchdog, with a way to view and undo it. */
function WatchdogNotice({
  now = false,
  change,
  regionLabel,
  busy,
  onView,
  onUndo,
}: {
  /** The notice after a search: "the watch is on" wording when now is set. */
  now?: boolean
  change: NonNullable<Answer['watchdogChange']>
  regionLabel: (code: string) => string
  busy: boolean
  onView: () => void
  onUndo: () => void
}) {
  const phrases = change.added
    .filter((item) => item.kind === 'PHRASE')
    .map((item) => `“${item.value}”`)
  const places = change.added
    .filter((item) => item.kind !== 'PHRASE')
    .map((item) => (item.kind === 'REGION' ? regionLabel(item.value) : item.value))
  const what = [
    phrases.join(' and '),
    places.length > 0 ? copy('search.region.in', { region: places.join(' and ') }) : '',
  ]
    .filter((part) => part !== '')
    .join(' ')
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <Radar className="size-3.5" aria-hidden />
      <span>{copy(now ? 'search.watch.now' : 'search.watch.updated', { what })}</span>
      <button
        type="button"
        onClick={onView}
        className="font-medium text-foreground hover:underline"
      >
        {copy('search.watch.view')}
      </button>
      <span>·</span>
      <button
        type="button"
        disabled={busy}
        onClick={onUndo}
        className="font-medium text-foreground hover:underline disabled:opacity-50"
      >
        {copy('search.watch.undo')}
      </button>
    </p>
  )
}

/** Opens the startup's data room: the profile page its open link shows, as the side panel's "Open profile" does. */
function OpenButton({ name, token, label }: { name: string; token: string; label: string }) {
  const { investor } = useInvestorContext()
  return (
    <Link
      to="/app/investor/$id/startup/$token"
      params={{ id: investor.id, token }}
      search={{ from: 'search' }}
      aria-label={`${label} ${name}`}
      className={secondaryLinkClasses({ size: 'sm' })}
    >
      {label}
    </Link>
  )
}
